// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a small picture of a board for All pads, the notes side and chat cards, drawn
// straight from the stored elements as SVG. It knows boxes, ellipses, diamonds, lines,
// text, pen strokes, frames and images, which is plenty at thumbnail size, and it never
// loads Excalidraw.

import {sceneBounds} from '@app/features/konvo_board/state/BoardLayout';
import {type BoardScope, default as KonvoBoards} from '@app/features/konvo_board/state/KonvoBoards';
import type {BoardElementLike} from '@fluxer/constants/src/BoardMerge';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useState} from 'react';

function num(value: unknown, fallback = 0): number {
	return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function color(value: unknown, fallback: string): string {
	return typeof value === 'string' && value && value !== 'transparent' ? value : fallback;
}

function Shape({el, files}: {el: BoardElementLike; files: Record<string, string>}) {
	const shape = <ShapeBody el={el} files={files} />;
	const opacity = num(el.opacity, 100);
	return opacity < 100 ? <g opacity={opacity / 100}>{shape}</g> : shape;
}

function ShapeBody({el, files}: {el: BoardElementLike; files: Record<string, string>}) {
	const x = num(el.x);
	const y = num(el.y);
	const w = num(el.width);
	const h = num(el.height);
	const stroke = color(el.strokeColor, 'none');
	const fill = el.backgroundColor && el.backgroundColor !== 'transparent' ? String(el.backgroundColor) : 'none';
	const sw = Math.max(1, num(el.strokeWidth, 1));
	const angle = num(el.angle);
	const transform = angle ? `rotate(${(angle * 180) / Math.PI} ${x + w / 2} ${y + h / 2})` : undefined;
	const rounded = el.roundness ? Math.min(16, Math.min(w, h) / 4) : 0;
	switch (el.type) {
		case 'rectangle':
			return (
				<rect
					x={x}
					y={y}
					width={w}
					height={h}
					rx={rounded}
					fill={fill}
					stroke={stroke}
					strokeWidth={sw}
					transform={transform}
				/>
			);
		case 'ellipse':
			return (
				<ellipse
					cx={x + w / 2}
					cy={y + h / 2}
					rx={w / 2}
					ry={h / 2}
					fill={fill}
					stroke={stroke}
					strokeWidth={sw}
					transform={transform}
				/>
			);
		case 'diamond':
			return (
				<polygon
					points={`${x + w / 2},${y} ${x + w},${y + h / 2} ${x + w / 2},${y + h} ${x},${y + h / 2}`}
					fill={fill}
					stroke={stroke}
					strokeWidth={sw}
					transform={transform}
				/>
			);
		case 'frame':
			return (
				<rect
					x={x}
					y={y}
					width={w}
					height={h}
					rx={12}
					fill="rgba(255,255,255,0.35)"
					stroke="rgba(20,20,15,0.16)"
					strokeWidth={2}
				/>
			);
		case 'arrow':
		case 'line':
		case 'freedraw': {
			const points = Array.isArray(el.points) ? (el.points as Array<[number, number]>) : [];
			if (points.length < 2) return null;
			const d = points.map(([px, py], i) => `${i === 0 ? 'M' : 'L'}${x + num(px)} ${y + num(py)}`).join(' ');
			return (
				<path
					d={d}
					fill="none"
					stroke={stroke === 'none' ? '#63656c' : stroke}
					strokeWidth={sw}
					strokeLinecap="round"
				/>
			);
		}
		case 'image': {
			const href = typeof el.fileId === 'string' ? files[el.fileId] : undefined;
			return href ? (
				<image
					href={href}
					x={x}
					y={y}
					width={w}
					height={h}
					preserveAspectRatio="xMidYMid slice"
					transform={transform}
				/>
			) : (
				<rect x={x} y={y} width={w} height={h} rx={8} fill="rgba(20,20,15,0.08)" />
			);
		}
		case 'text': {
			const size = num(el.fontSize, 16);
			const lines = String(el.text ?? '').split('\n');
			return (
				<text
					x={x}
					y={y + size}
					fontSize={size}
					fill={color(el.strokeColor, '#1d1e22')}
					fontFamily="inherit"
					transform={transform}
				>
					{lines.map((line, i) => (
						<tspan key={i} x={x} dy={i === 0 ? 0 : size * 1.25}>
							{line}
						</tspan>
					))}
				</text>
			);
		}
		default:
			return null;
	}
}

/** Draws `elements` scaled into the box it is given; images come from `scope`'s board when set. */
export const BoardPreview = observer(function BoardPreview({
	scope,
	className,
	style,
	padding = 24,
}: {
	scope: BoardScope;
	className?: string;
	style?: React.CSSProperties;
	padding?: number;
}) {
	const doc = KonvoBoards.get(scope);
	const [files, setFiles] = useState<Record<string, string>>({});

	useEffect(() => {
		if (!doc.loaded && doc.status !== 'loading') void doc.load();
	}, [doc]);

	const elements = doc.liveElements;
	const imageIds = elements
		.filter((el) => el.type === 'image' && typeof el.fileId === 'string')
		.map((el) => String(el.fileId));
	const imageKey = imageIds.join(',');

	useEffect(() => {
		if (!imageKey) return;
		let live = true;
		void doc.loadFiles(imageKey.split(',')).then((loaded) => {
			if (live) setFiles(Object.fromEntries(loaded.map((file) => [file.id, file.dataURL])));
		});
		return () => {
			live = false;
		};
	}, [doc, imageKey]);

	const bounds = sceneBounds(elements);
	if (!bounds) return <div className={className} style={style} />;
	// Frame labels sit above their frame; text inside cards is drawn after its container.
	const ordered = [...elements].sort((a, b) => (a.type === 'frame' ? -1 : 0) - (b.type === 'frame' ? -1 : 0));
	return (
		<svg
			className={className}
			style={style}
			viewBox={`${bounds.x - padding} ${bounds.y - padding} ${bounds.width + padding * 2} ${bounds.height + padding * 2}`}
			preserveAspectRatio="xMidYMin meet"
			role="img"
			aria-label="Board preview"
		>
			{ordered.map((el) => (
				<Shape key={el.id} el={el} files={files} />
			))}
		</svg>
	);
});
